
import varianceAnalysisConstants from "../constants";
import { UPDATE_RU_SETTINGS } from "../utils/constants";
import { storeGet } from '@/store/store-utils';

export default {
  namespaced: true,

  state: {
    reportingunitWorkflowSettings: {},
    reportingunitOtherSettings: {},
    ruFilters: {},
    myReportingUnits: [],
    myReportingUnitsMeta: { total: 0 },
    myReportingUnitsFilter: null,
    myReportingUnitsCache: {},
    materialityData: [],
    materialityCache: {},
    ruDetail: {},
    ruDetailCache: {},
    editingMaterialityPayload: null,
    // Materiality editing data
    materialityGLList: {},
    materialityFormattedGLData: [],
    materialityAddeditGLData: {},
    materialityFormattedRUData: {},
    materialityFormattedRUErrorData: {},
    // View mode materiality level data
    viewRULevelMaterialityData: [],
    viewGLLevelMaterialityData: [],
    viewSLLevelMaterialityData: [],
    // Threshold page state
    allReportingUnits: [],
    allReportingUnitsMeta: { totalCount: 0 },
    glList: {},
    glCodeNameMap: {},
    glReportingTypeMap: {},
    glCodeIdMap: {},
    allGLs: [],
    pendingApprovalRequests: [],
    myMaterialityData: {},
    myMaterialityAbsoluteThresholdValues: {},
    myMaterialityLoading: false,
    // RU Insights data
    ruInsights: {},
    ruInsightsLoading: false,
    ruFiltersCache: {},
    allReportingUnitsCache: {},
  },

  mutations: {
    SET_MATERIALITY_DATA(state, data) {
      state.materialityData = data;
    },
    SET_MATERIALITY_LOADING(state, loading) {
      state.materialityLoading = loading;
    },
    SET_REPORTING_UNIT_WORKFLOW_SETTINGS(state, settings) {
      state.reportingunitWorkflowSettings = settings;
    },
    SET_REPORTING_UNIT_OTHER_SETTINGS(state, payload) {
      const {type, otherSettings} = payload;
      state.reportingunitOtherSettings[type] = otherSettings;
    },
    SET_RU_FILTERS(state, filters) {
      state.ruFilters = filters;
    },
    SET_RU_FILTERS_CACHE(state, { key, payload }) {
      state.ruFiltersCache = { ...state.ruFiltersCache, [key]: payload };
    },
    SET_ALL_REPORTING_UNITS_CACHE(state, { key, payload }) {
      state.allReportingUnitsCache = { ...state.allReportingUnitsCache, [key]: payload };
    },
    CLEAR_RU_CACHES(state) {
      state.myReportingUnitsCache = {};
      state.allReportingUnitsCache = {};
      state.materialityCache = {};
      state.ruDetailCache = {};
      state.ruDetail = {};
    },
    INVALIDATE_RU_DETAIL_CACHES(state, reportingUnitIds = []) {
      const ids = (reportingUnitIds || []).map(id => String(id)).filter(Boolean);

      if (ids.length === 0) {
        state.ruDetailCache = {};
        state.ruDetail = {};
        return;
      }

      const nextCache = { ...state.ruDetailCache };
      ids.forEach(id => {
        delete nextCache[id];
      });
      state.ruDetailCache = nextCache;

      if (state.ruDetail?._id && ids.includes(String(state.ruDetail._id))) {
        state.ruDetail = {};
      }
    },
    SET_MY_REPORTING_UNITS(state, { data, meta, filter }) {
      state.myReportingUnits = data;
      state.myReportingUnitsMeta = meta;
      if (filter) {
        state.myReportingUnitsFilter = filter;
      }
    },
    SET_MY_REPORTING_UNITS_CACHE(state, { key, payload }) {
      state.myReportingUnitsCache = {
        ...state.myReportingUnitsCache,
        [key]: payload,
      };
    },
    SET_MATERIALITY_CACHE(state, { key, payload }) {
      state.materialityCache = {
        ...state.materialityCache,
        [key]: payload,
      };
    },
    SET_RU_DETAIL(state, detail) {
      state.ruDetail = detail;
    },
    SET_RU_DETAIL_CACHE(state, { id, detail }) {
      state.ruDetailCache = {
        ...state.ruDetailCache,
        [id]: detail,
      };
    },
    SET_EDITING_MATERIALITY_PAYLOAD(state, payload) {
      state.editingMaterialityPayload = payload;
    },
    SET_MATERIALITY_GL_LIST(state, glList) {
      state.materialityGLList = glList;
    },
    SET_MATERIALITY_FORMATTED_GL_DATA(state, data) {
      state.materialityFormattedGLData = data;
    },
    SET_MATERIALITY_ADDEDIT_GL_DATA(state, data) {
      state.materialityAddeditGLData = data;
    },
    SET_MATERIALITY_FORMATTED_RU_DATA(state, data) {
      state.materialityFormattedRUData = data;
    },
    SET_MATERIALITY_FORMATTED_RU_ERROR_DATA(state, data) {
      state.materialityFormattedRUErrorData = data;
    },
    SET_VIEW_RU_LEVEL_MATERIALITY_DATA(state, data) {
      state.viewRULevelMaterialityData = data;
    },
    SET_VIEW_GL_LEVEL_MATERIALITY_DATA(state, data) {
      state.viewGLLevelMaterialityData = data;
    },
    SET_VIEW_SL_LEVEL_MATERIALITY_DATA(state, data) {
      state.viewSLLevelMaterialityData = data;
    },
    SET_ALL_REPORTING_UNITS(state, { data, meta }) {
      state.allReportingUnits = data;
      state.allReportingUnitsMeta = meta;
    },
    SET_GL_LIST(state, glList) {
      state.glList = glList;
    },
    SET_GL_CODE_NAME_MAP(state, map) {
      state.glCodeNameMap = map;
    },
    SET_GL_REPORTING_TYPE_MAP(state, map) {
      state.glReportingTypeMap = map;
    },
    
    SET_GL_CODE_ID_MAP(state, map) {
      state.glCodeIdMap = map;
    },
    SET_ALL_GLS(state, gls) {
      state.allGLs = gls;
    },
    SET_PENDING_APPROVAL_REQUESTS(state, requests) {
      state.pendingApprovalRequests = requests;
    },
    SET_MY_MATERIALITY_DATA(state, data) {
      state.myMaterialityData = data;
    },
    SET_MY_MATERIALITY_ABSOLUTE_THRESHOLD_VALUES(state, data) {
      state.myMaterialityAbsoluteThresholdValues = data;
    },
    SET_MY_MATERIALITY_LOADING(state, loading) {
      state.myMaterialityLoading = loading;
    },
    SET_RU_INSIGHTS(state, data) {
      state.ruInsights = data;
    },
    SET_RU_INSIGHTS_LOADING(state, loading) {
      state.ruInsightsLoading = loading;
    },
    RESET_RU_INSIGHTS(state) {
      state.ruInsights = {};
      state.ruInsightsLoading = false;
    },
    RESET_STATE(state) {
      // Don't reset ruFilters to allow caching
      // state.ruFilters = {};
      state.myReportingUnits = [];
      state.myReportingUnitsMeta = { total: 0 };
      state.myReportingUnitsFilter = null;
      // state.myReportingUnitsCache = {};
      state.materialityData = [];
      state.ruDetail = {};
      state.allReportingUnits = [];
      state.allReportingUnitsMeta = { totalCount: 0 };
      state.glList = {};
      state.glCodeNameMap = {};
      state.glCodeIdMap = {};
      state.allGLs = [];
      state.pendingApprovalRequests = [];
      state.myMaterialityData = {};
      state.ruInsights = {};
      state.ruInsightsLoading = false;
    },
    RESET_FILTERS(state) {
      state.ruFilters = {};
    },
    RESET_MATERIALITY_EDITING_STATE(state) {
      state.editingMaterialityPayload = null;
      state.materialityGLList = {};
      state.materialityFormattedGLData = [];
      state.materialityAddeditGLData = {};
      state.materialityFormattedRUData = {};
      state.materialityFormattedRUErrorData = {};
      state.viewRULevelMaterialityData = [];
      state.viewGLLevelMaterialityData = [];
      state.viewSLLevelMaterialityData = [];
    }
  },

  actions: {
    async updateWorkflowSettings({ commit }, { reportingUnitId, workflowData , axiosInstance, showNotification = true }) {
      try {
        const url =
          varianceAnalysisConstants.API_ENDPOINTS.ENABLE_DISABLE_WORKFLOW +
          reportingUnitId;
        const response = await axiosInstance.post(url, {
          data: [workflowData],
        });

        if (response?.data?.status !== varianceAnalysisConstants.STATUS.SUCCESS) {
          throw new Error(
            response?.data?.message ||
              varianceAnalysisConstants.MESSAGES.FAILED_TO_UPDATE_WORKFLOW
          );
        }
        const updatedSettings = response?.data?.data[0] || {};
        commit(UPDATE_RU_SETTINGS.MUTATORS.WORKFLOW, updatedSettings);
        commit('CLEAR_RU_CACHES');
        if (showNotification) {
          commit('sbSuccess', varianceAnalysisConstants.MESSAGES.SETTINGS_UPDATED_SUCCESSFULLY, { root: true });
        }
      } catch (error) {
        console.error("Error updating workflow settings:", error);
        commit('sbError', error.message || varianceAnalysisConstants.MESSAGES.FAILED_TO_UPDATE_WORKFLOW, { root: true });
        throw error;
      }
    },

    async updateBulkWorkflowSettings({ commit }, { reportingUnitIds, workflowData, otherSettingsData, axiosInstance, showNotification = true }) {
      try {
        let commonData = {};
        if (workflowData) {
            commonData = { ...commonData, ...workflowData };
        }
           if (otherSettingsData) {
             if (otherSettingsData.sodEnabled !== undefined) commonData.sod_enabled = otherSettingsData.sodEnabled ? true : false;
             if (otherSettingsData.autoSubmissionEnabled !== undefined) commonData.auto_regional_commentary_submission_enabled = otherSettingsData.autoSubmissionEnabled;
             if (otherSettingsData.bulkapprove_control !== undefined) commonData.bulkapprove_control = otherSettingsData.bulkapprove_control;
        }

        /*
          Correct structure expected by backend for bulk update:
          {
            data: [
              { id: ["ID1", "ID2"], ...settings }
            ]
          }
        */
        const data = [{
            ruids: reportingUnitIds,
            ...commonData
        }];

        const response = await axiosInstance.post(
          varianceAnalysisConstants.API_ENDPOINTS.ENABLE_DISABLE_BULK,
          {
            data: data,
          }
        );

        if (response?.data?.status !== varianceAnalysisConstants.STATUS.SUCCESS) {
          throw new Error(
            response?.data?.message || varianceAnalysisConstants.MESSAGES.FAILED_TO_UPDATE_BULK_WORKFLOW_SETTINGS
          );
        }

        commit('CLEAR_RU_CACHES');
        
        if (showNotification) {
          commit('sbSuccess', varianceAnalysisConstants.MESSAGES.SETTINGS_UPDATED_SUCCESSFULLY, { root: true });
        }
      } catch (error) {
        console.error("Error updating bulk workflow settings:", error);
        commit('sbError', error.message || varianceAnalysisConstants.MESSAGES.FAILED_TO_UPDATE_BULK_WORKFLOW_SETTINGS, { root: true });
        throw error;
      }
    },

    async updateBulkApproveSettings({ commit }, { reportingUnitId, reportingUnitIds, bulkapprove_control, workflowData, otherSettingsData, axiosInstance, showNotification = true }) {
      try {
        let data = [];
        
        let commonData = {};
        if (workflowData) {
            commonData = { ...commonData, ...workflowData };
        }
        if (otherSettingsData) {
          // Mapping other settings if the backend expects flattened structure
          // NOTE: Adjust keys based on expected backend payload for bulk endpoint
          if (otherSettingsData.sodEnabled !== undefined) commonData.sod_enabled = otherSettingsData.sodEnabled ? true : false;
            if (otherSettingsData.autoSubmissionEnabled !== undefined) commonData.auto_regional_commentary_submission_enabled = otherSettingsData.autoSubmissionEnabled; // Adjust key if needed (e.g. isRegionalEnabled?)
            // Assuming bulkapprove_control is passed separately or in otherSettingsData
            if (otherSettingsData.bulkapprove_control !== undefined) commonData.bulkapprove_control = otherSettingsData.bulkapprove_control;
        }
        // Override explicit argument if provided
        if (bulkapprove_control !== undefined) commonData.bulkapprove_control = bulkapprove_control;

        if (reportingUnitIds && Array.isArray(reportingUnitIds) && reportingUnitIds.length > 0) {
             data = [{
                id: reportingUnitIds,
                ...commonData
             }];
        } else if (reportingUnitId) {
             data = [{
                id: reportingUnitId,
                ...commonData
            }];
        }

        const response = await axiosInstance.post(
          varianceAnalysisConstants.API_ENDPOINTS.ENABLE_DISABLE_BULK_APPROVE,
          {
            data: data,
          }
        );

        if (response?.data?.status !== varianceAnalysisConstants.STATUS.SUCCESS) {
          throw new Error(
            response?.data?.message ||
              varianceAnalysisConstants.MESSAGES.FAILED_TO_UPDATE_BULK_APPROVE_SETTINGS
          );
        }

        commit('CLEAR_RU_CACHES');
        
        // Update state for each impacted item
        if (response?.data?.data && Array.isArray(response.data.data)) {
             response.data.data.forEach(item => {
                 const payload = {
                    type: UPDATE_RU_SETTINGS.TYPES.BULK_APPROVE,
                    otherSettings: item || {}
                }
                commit(UPDATE_RU_SETTINGS.MUTATORS.OTHER_SETTINGS, payload);
             });
        }
        
        if (showNotification) {
          commit('sbSuccess', varianceAnalysisConstants.MESSAGES.SETTINGS_UPDATED_SUCCESSFULLY, { root: true });
        }
      } catch (error) {
        console.error("Error updating bulk approve settings:", error);
        commit('sbError', error.message || varianceAnalysisConstants.MESSAGES.FAILED_TO_UPDATE_BULK_APPROVE_SETTINGS, { root: true });
        throw error;
      }
    },

    async updateAutoSubmissionSettings({ commit }, { reportingUnitId, auto_regional_commentary_submission_enabled, axiosInstance, showNotification = true }) {
      try {
        const response = await axiosInstance.post(
          varianceAnalysisConstants.API_ENDPOINTS.ENABLE_DISABLE_REGIONAL_COMMENTARY_AUTO_SUBMISSION,
          {
            reportingUnitId: reportingUnitId,
            status: auto_regional_commentary_submission_enabled,
          }
        );

        if (response?.data?.status !== varianceAnalysisConstants.STATUS.SUCCESS) {
          console.error('[updateAutoSubmissionSettings] API Error Response:', response?.data);
          throw new Error(
            response?.data?.message ||
              varianceAnalysisConstants.MESSAGES.FAILED_TO_UPDATE_AUTO_SUBMISSION_SETTINGS
          );
        }
        const payload = {
          type: UPDATE_RU_SETTINGS.TYPES.AUTO_SUBMISSION,
          otherSettings: response?.data?.data[0] || {}
        }
        commit(UPDATE_RU_SETTINGS.MUTATORS.OTHER_SETTINGS, payload);
        if (showNotification) {
          commit('sbSuccess', varianceAnalysisConstants.MESSAGES.SETTINGS_UPDATED_SUCCESSFULLY, { root: true });
        }
      } catch (error) {
        console.error("Error updating auto submission settings:", error);
        if (error.response) {
            console.error('[updateAutoSubmissionSettings] HTTP Error Response:', error.response.data);
        }
        commit('sbError', error.message || varianceAnalysisConstants.MESSAGES.FAILED_TO_UPDATE_AUTO_SUBMISSION_SETTINGS, { root: true });
        throw error;
      }
    },

    async updateSodStatus({ commit }, { reportingUnitId, sod_status, axiosInstance, showNotification = true }) {
      try {
        const response = await axiosInstance.post(
          varianceAnalysisConstants.API_ENDPOINTS.UPDATE_SOD_STATUS,
          {
            reportingUnitId: reportingUnitId,
            sod_status: sod_status,
          }
        );


        if (response?.data?.status !== varianceAnalysisConstants.STATUS.SUCCESS) {
          throw new Error(
            response?.data?.message || varianceAnalysisConstants.MESSAGES.FAILED_TO_UPDATE_SOD_STATUS
          );
        }
        const payload = {
          type: UPDATE_RU_SETTINGS.TYPES.SOD,
          otherSettings: response?.data?.data[0] || {}
        }
        commit(UPDATE_RU_SETTINGS.MUTATORS.OTHER_SETTINGS, payload);
        commit('CLEAR_RU_CACHES');
        if (showNotification) {
          commit('sbSuccess', varianceAnalysisConstants.MESSAGES.SETTINGS_UPDATED_SUCCESSFULLY, { root: true });
        }
      } catch (error) {
        console.error("Error updating SOD status:", error);
        commit('sbError', error.message || varianceAnalysisConstants.MESSAGES.FAILED_TO_UPDATE_SOD_STATUS, { root: true });
        throw error;
      }
    },

    async updateOtherSettings(
      { dispatch },
      { reportingUnitId, otherSettingsData, isAnyControlWorkflowEnabled , axiosInstance, showNotification = true }
    ) {
      try {
        const promises = [];
        // 1. Update Bulk Approve (only if control workflows are enabled)
        if (isAnyControlWorkflowEnabled) {
          promises.push(
            dispatch(UPDATE_RU_SETTINGS.DISPATCHERS.UPDATE_BULK_APPROVE, {
              reportingUnitId,
              bulkapprove_control: otherSettingsData?.bulkapprove_control || false,
              axiosInstance,
              showNotification
            })
          );
        }

        // 2. Update Auto Submission
        promises.push(
          dispatch(UPDATE_RU_SETTINGS.DISPATCHERS.UPDATE_AUTO_SUBMISSION, {
            reportingUnitId,
            status: otherSettingsData?.autoSubmissionEnabled || false,
            axiosInstance,
            showNotification
          })
        );

        // 3. Update SOD
        promises.push(
          dispatch(UPDATE_RU_SETTINGS.DISPATCHERS.UPDATE_SOD_STATUS, {
            reportingUnitId,
            sod_status: otherSettingsData?.sodEnabled ? true : false,
            axiosInstance,
            showNotification
          })
        );

        // Execute all API calls
       const results = await Promise.allSettled(promises);
       const rejected = results?.filter((result) => result?.status === varianceAnalysisConstants.STATUS.REJECTED);
        if (rejected.length) {
          const error = new Error(varianceAnalysisConstants.MESSAGES.FAILED_TO_UPDATE_SETTINGS);
          error.causes = rejected?.map((result) => result?.reason || varianceAnalysisConstants.SOD.MESSAGES.UNKNOWN_ERROR);
          throw error;
        }

        return results?.map((result) => result?.value);

      } catch (error) {
        console.error("Error updating other settings:", error);
        throw error;
      }
    },

    /**
     * Fetches RU Insights data for the RU Info Dialog
     * @param {Object} payload - Contains filter object with ruId, ruType and client
     * @returns {Promise} - Resolves on success, rejects on failure
     */
    async fetchRUInsights({ commit }, { filter, client, axiosInstance }) {
      commit('SET_RU_INSIGHTS_LOADING', true);
      try {
        const response = await axiosInstance.post(
          varianceAnalysisConstants.API_ENDPOINTS.GET_RU_INSIGHTS,
          { filter, client }
        );

        if (response?.data?.status === varianceAnalysisConstants.STATUS.SUCCESS) {
          commit('SET_RU_INSIGHTS', response.data.data || {});
          commit('sbSuccess', varianceAnalysisConstants.MESSAGES.RU_INSIGHTS_FETCHED_SUCCESSFULLY, { root: true });
        } else {
          console.warn('[reportingUnit][fetchRUInsights] non-success status', response?.data);
          throw new Error(
            response?.data?.message || varianceAnalysisConstants.MESSAGES.FAILED_TO_FETCH_RU_INSIGHTS
          );
        }
      } catch (error) {
        console.error('[reportingUnit] fetchRUInsights error:', error);
        commit('SET_RU_INSIGHTS', {});
        commit('sbError', error.message || varianceAnalysisConstants.MESSAGES.FAILED_TO_FETCH_RU_INSIGHTS, { root: true });
        throw error;
      } finally {
        commit('SET_RU_INSIGHTS_LOADING', false);
      }
    },

    /**
     * Approves Reporting Unit at Local or Regional level
     * @param {Object} payload 
     * @param {Object} payload.filter - { type: 'local'|'regional', cycleids: [], cycletype: '', reporting_type: '' }
     * @param {Object} payload.data - { approvalcomment: '' }
     * @param {Object} payload.axiosInstance
     */
    async approveReportingUnitLevel({ filter, data, axiosInstance }) {
      const url = "/v3/varianceanalysis/analysis/rulevelapproval/";
      try {
        const response = await axiosInstance.post(url, { filter, data });
        if (response.data.status === "success") {
          return response.data;
        } else {
          throw new Error(response.data.message || "Approval failed");
        }
      } catch (error) {
        console.error('[reportingUnit] approveReportingUnitLevel error:', error);
        throw error;
      }
    },

    async fetchRUFilters({ commit, state, rootState }, { filter, axiosInstance }) {
      // Check if state already has filters populated (Level 1 Cache)
      if (state.ruFilters && Object.keys(state.ruFilters).length > 0) {
        return; 
      }

      // Check specific cache key (Level 2 Cache)
      const cacheKey = JSON.stringify(filter || {});
      if (state.ruFiltersCache?.[cacheKey]) {
        commit('SET_RU_FILTERS', state.ruFiltersCache[cacheKey]);
        return;
      }

      try {
        const response = await storeGet(
          axiosInstance,
          rootState,
          varianceAnalysisConstants.API_ENDPOINTS.GET_RU_FILTERS,
          filter
        );
        if (response?.data?.status === varianceAnalysisConstants.STATUS.SUCCESS) {
          commit('SET_RU_FILTERS', response.data.data);
          commit('SET_RU_FILTERS_CACHE', { key: cacheKey, payload: response.data.data });
        } else {
           throw new Error(response?.data?.message || 'Failed to fetch RU filters');
        }
      } catch (error) {
        console.error('[reportingUnit] fetchRUFilters error', error);
        commit('sbError', error.message || 'Failed to fetch RU filters', { root: true });
        throw error;
      }
    },

    async fetchMyReportingUnits({ commit, state, rootState }, { filter, client, axiosInstance, forceRefresh = false }) {
      try {
        // Normalize cache key by extracting only relevant fields in sorted order
        const normalizeFilterForCache = (filter, client) => {
          const normalized = {
            client: client || '',
            cycletype: filter?.cycletype || '',
            year: filter?.year || '',
            period: filter?.period || '',
            network: Array.isArray(filter?.network) ? filter.network.sort().join(',') : (filter?.network || ''),
            region: Array.isArray(filter?.region) ? filter.region.sort().join(',') : (filter?.region || ''),
            country: Array.isArray(filter?.country) ? filter.country.sort().join(',') : (filter?.country || ''),
            reportingunit: Array.isArray(filter?.reportingunit) ? filter.reportingunit.sort().join(',') : (filter?.reportingunit || ''),
            ruTypes: Array.isArray(filter?.ruTypes) ? filter.ruTypes.sort().join(',') : (filter?.ruTypes || ''),
            user_id: filter?.user_id || '',
            page: filter?.page || 0,
            limit: filter?.limit || 25
          };
          // Create consistent key from sorted keys
          return Object.keys(normalized).sort().map(k => `${k}:${normalized[k]}`).join('|');
        };
        
        const cacheKey = normalizeFilterForCache(filter, client);

        if (!forceRefresh) {
          const cachedEntry = state.myReportingUnitsCache?.[cacheKey];
          if (cachedEntry) {
            commit('SET_MY_REPORTING_UNITS', {
              data: cachedEntry.data,
              meta: cachedEntry.meta,
              filter: cachedEntry.filter
            });
            return { fromCache: true };
          }
        }
        

        const params = { ...filter };
        if (client) {
            params.cid = client;
        }

        const response = await storeGet(
          axiosInstance, rootState,
          varianceAnalysisConstants.API_ENDPOINTS.GET_MY_RUS,
          params
        );
        if (response?.data?.status === varianceAnalysisConstants.STATUS.SUCCESS) {
          commit('SET_MY_REPORTING_UNITS', {
            data: response.data.data,
            meta: response.data.meta,
            filter: filter
          });
          commit('SET_MY_REPORTING_UNITS_CACHE', {
            key: cacheKey,
            payload: {
              data: response.data.data,
              meta: response.data.meta,
              filter
            }
          });
          return { fromCache: false };
        } else {
            throw new Error(response?.data?.message || 'Failed to fetch reporting units');
        }
      } catch (error) {
        console.error('[reportingUnit] fetchMyReportingUnits error', error);
        commit('sbError', error.message || 'Failed to fetch reporting units', { root: true });
        throw error;
      }
    },

    async fetchMateriality({ commit, state, rootState }, { filter, client, axiosInstance, forceRefresh = false }) {
      try {
        // Generate cache key using the same normalization function
        const normalizeFilterForCache = (filterObj) => {
          const relevantFields = ['year', 'period', 'network', 'region', 'country', 'reportingunit', 'ruTypes', 'page', 'limit', 'user_id'];
          const normalized = {};
          relevantFields.forEach(field => {
            if (filterObj[field] !== undefined && filterObj[field] !== null && filterObj[field] !== 'all') {
              let val = filterObj[field];
              if (Array.isArray(val)) {
                val = [...val].sort();
              }
              normalized[field] = val;
            }
          });
          return JSON.stringify(normalized);
        };

        const cacheKey = `${client}_${normalizeFilterForCache(filter || {})}`;

        // Check cache
        if (!forceRefresh && state.materialityCache[cacheKey]) {
          commit('SET_MATERIALITY_DATA', state.materialityCache[cacheKey]);
          return { fromCache: true };
        }

        const response = await storeGet(
          axiosInstance, rootState,
          varianceAnalysisConstants.API_ENDPOINTS.CALCULATE_MATERIALITY,
          filter
        );
        if (response?.data?.status === varianceAnalysisConstants.STATUS.SUCCESS) {
          const data = response.data.data;
          commit('SET_MATERIALITY_DATA', data);
          // Store in cache
          commit('SET_MATERIALITY_CACHE', { key: cacheKey, payload: data });
          return { fromCache: false };
        } else {
             throw new Error(response?.data?.message || 'Failed to fetch materiality data');
        }
      } catch (error) {
        console.error('[reportingUnit] fetchMateriality error', error);
        commit('sbError', error.message || 'Failed to fetch materiality data', { root: true });
        throw error;
      }
    },

    async fetchRUDetail({ commit, state, rootState }, { filter, axiosInstance, forceRefresh = false }) {
      try {
        const targetId = filter._id || (filter.reportingunit && filter.reportingunit[0]);
        const targetIdKey = targetId ? String(targetId) : null;

        if (!forceRefresh && targetIdKey && state.ruDetailCache?.[targetIdKey]) {
           commit('SET_RU_DETAIL', state.ruDetailCache[targetIdKey]);
           return;
        }

        const response = await storeGet(
          axiosInstance, rootState,
          varianceAnalysisConstants.API_ENDPOINTS.GET_MY_RU_DETAIL,
          filter
        );

        if (response?.data?.status === varianceAnalysisConstants.STATUS.SUCCESS) {
          const detail = response?.data?.data?.[0]?.reportingunit || {};
          commit('SET_RU_DETAIL', detail);
          if (targetIdKey) {
             commit('SET_RU_DETAIL_CACHE', { id: targetIdKey, detail });
          }
        } else {
            throw new Error(response?.data?.message || 'Failed to fetch RU detail');
        }
      } catch (error) {
        console.error('[reportingUnit] fetchRUDetail error', error);
        commit('sbError', error.message || 'Failed to fetch RU detail', { root: true });
        throw error;
      }
    },

    /**
     * Fetches cycle summaries for accordion expansion
     * @param {Object} payload - Contains cycleIds, reportingunit, period and cycletype
     * @returns {Object} - The cycle summary data from the API
     */
    async getCycleSummaries({ commit, rootState }, { cycleIds, reportingunit, period, cycletype, axiosInstance }) {
      try {
        const response = await storeGet(
          axiosInstance, rootState,
          varianceAnalysisConstants.API_ENDPOINTS.GET_CYCLE_SUMMARIES,
          {
            cycleIds,
            reportingunit,
            period,
            cycletype,
          }
        );

        if (response?.data?.status !== varianceAnalysisConstants.STATUS.SUCCESS) {
          const errorMessage = response?.data?.message || 'Failed to fetch cycle summaries';
          commit('sbError', errorMessage, { root: true });
          throw new Error(errorMessage);
        }

        return response?.data?.data || [];
      } catch (error) {
        console.error('[reportingUnit] Error fetching cycle summaries:', error);
        commit('sbError', error.message || 'Failed to fetch cycle summaries', { root: true });
        throw error;
      }
    },

    /**
     * Fetches all reporting units with server-side pagination and filtering for Threshold page.
     * The backend handles pagination (page, limit) and filtering (network, region, country, ruids).
     * @param {Object} payload.filter - { limit, page, network?, region?, country?, ruids? }
     * @param {Object} payload.axiosInstance - Axios instance
     * @param {boolean} [payload.forceRefresh=false] - Bypass cache when true
     */
    async fetchAllReportingUnits({ commit, state, rootState }, { filter, axiosInstance, forceRefresh = false }) {
      const cacheKey = JSON.stringify(filter || {});
      
      // Level 1: Check if we already have data in state that matches requirement (approximate)
      // If asking for "all" (high limit) and we have data, we can skip if we trust state.
      // But safer to rely on explicit cacheKey check (Level 2) which we introduced.
      
      if (!forceRefresh && state.allReportingUnitsCache?.[cacheKey]) {
        const cached = state.allReportingUnitsCache[cacheKey];
        commit('SET_ALL_REPORTING_UNITS', {
          data: cached.data,
          meta: cached.meta
        });
        return;
      }

      try {
        const response = await storeGet(
          axiosInstance,
          rootState,
          varianceAnalysisConstants.API_ENDPOINTS.GET_ALL_REPORTING_UNITS,
          filter
        );
        if (response?.data?.status !== varianceAnalysisConstants.STATUS.SUCCESS) {
          throw new Error(response?.data?.message || varianceAnalysisConstants.MESSAGES.FAILED_TO_FETCH_REPORTING_UNITS);
        }

        const data = response.data.data || [];
        const meta = response.data.meta || { totalCount: response.data.data?.length || 0 };

        commit('SET_ALL_REPORTING_UNITS', { data, meta });
        commit('SET_ALL_REPORTING_UNITS_CACHE', { 
          key: cacheKey, 
          payload: { data, meta } 
        });
      } catch (error) {
        console.error('[reportingUnit] fetchAllReportingUnits error:', error);
        commit('sbError', error.message || varianceAnalysisConstants.MESSAGES.FAILED_TO_FETCH_REPORTING_UNITS, { root: true });
        throw error;
      }
    },

    /**
     * Fetches GL list and processes it into organized structure
     * @param {Object} payload - Contains axiosInstance and optional reportingType filter
     */
    async fetchGLList({ commit, rootState }, { axiosInstance, reportingType }) {
      try {
        let glData = [];
        
        if (reportingType) {
          // If reportingType is specified, fetch only that type
          const filter = { reporting_type: reportingType };
          const response = await storeGet(
            axiosInstance,
            rootState,
            varianceAnalysisConstants.API_ENDPOINTS.GET_GL_LIST,
            filter
          );
          
          if (response?.data?.status !== varianceAnalysisConstants.STATUS.SUCCESS) {
            throw new Error(response?.data?.message || varianceAnalysisConstants.MESSAGES.FAILED_TO_FETCH_GL_LIST);
          }
          glData = response.data.data || [];
        } else {
          // If no reportingType specified, fetch both PL and BS
          // This ensures we have a complete list for validation (e.g. bulk upload)
          const plPromise = storeGet(
            axiosInstance,
            rootState,
            varianceAnalysisConstants.API_ENDPOINTS.GET_GL_LIST,
            { reporting_type: 'pl' }
          );
          
          const bsPromise = storeGet(
            axiosInstance,
            rootState,
            varianceAnalysisConstants.API_ENDPOINTS.GET_GL_LIST,
            { reporting_type: 'bs' }
          );
          
          const [plResponse, bsResponse] = await Promise.all([plPromise, bsPromise]);
          
          if (plResponse?.data?.status === varianceAnalysisConstants.STATUS.SUCCESS) {
            glData = [...glData, ...(plResponse.data.data || [])];
          }
          
          if (bsResponse?.data?.status === varianceAnalysisConstants.STATUS.SUCCESS) {
            // Avoid duplicates if any (though codes should be unique or handled)
            const plCodes = new Set(glData.map(item => item.code));
            const bsData = (bsResponse.data.data || []).filter(item => !plCodes.has(item.code));
            glData = [...glData, ...bsData];
          }
        }

        const glList = { primary: [] };
        const glCodeNameMap = {};
        const glReportingTypeMap = {};
        const glCodeIdMap = {};
        const allGLs = [];

        // Process GL data into organized structure
        for (const item of glData) {
          glReportingTypeMap[item.code]= item.reporting_type
          glCodeNameMap[item.code] = item.name;
          glCodeIdMap[item.code] = item._id;
          allGLs.push(item.code);

          if (item.parent) {
            glList[item.parent] = glList[item.parent] || [];
            glList[item.parent].push(item);
          } else {
            glList['NA'] = glList['NA'] || [];
            glList['NA'].push(item);
          }
        }

        // Add ungrouped entry and sort by order
        glList.primary.push({
          code: 'NA',
          order: 99999,
          name: 'Ungrouped: Other sub-schedules and further Analysis Accounts'
        });

        // Helper function to reorder arrays by a property
        const reorder = (arr, prop) => {
          return [...arr].sort((a, b) => (a[prop] || 0) - (b[prop] || 0));
        };

        glList.primary = reorder(glList.primary, 'order');
        for (const item of glList.primary) {
          glList[item.code] = reorder(glList[item.code] || [], 'order');
        }

        // Commit all processed data
        
        commit('SET_GL_LIST', glList);
        commit('SET_GL_CODE_NAME_MAP', glCodeNameMap);
        commit('SET_GL_CODE_ID_MAP', glCodeIdMap);
        commit('SET_ALL_GLS', allGLs);
        commit('SET_GL_REPORTING_TYPE_MAP', glReportingTypeMap);
        // Also commit to materiality GL list for AddMateriality component
        commit('SET_MATERIALITY_GL_LIST', glList);
        
        // commit('sbSuccess', 'GL List loaded successfully', { root: true });
      } catch (error) {
        // console.error('[reportingUnit] fetchGLList error:', error);
        commit('sbError', error.message || varianceAnalysisConstants.MESSAGES.FAILED_TO_FETCH_GL_LIST, { root: true });
        throw error;
      }
    },

    /**
     * Fetches pending approval requests for materiality
     * @param {Object} payload - Contains axiosInstance
     */
    async fetchPendingApprovalRequests({ commit, rootState }, { client, axiosInstance }) {
      try {
        const params = {};
        if (client) {
          params.cid = client;
        }
        const response = await storeGet(
          axiosInstance, rootState,
          varianceAnalysisConstants.API_ENDPOINTS.GET_PENDING_APPROVAL_REQUESTS,
          params
        );
        if (response?.data?.status !== varianceAnalysisConstants.STATUS.SUCCESS) {
          throw new Error(response?.data?.message || varianceAnalysisConstants.MESSAGES.FAILED_TO_FETCH_PENDING_REQUESTS);
        }

        const requests = response.data.data || [];
        commit('SET_PENDING_APPROVAL_REQUESTS', requests);
      } catch (error) {
        commit('sbError', error.message || varianceAnalysisConstants.MESSAGES.FAILED_TO_FETCH_PENDING_REQUESTS, { root: true });
        throw error;
      }
    },

    /**
     * Fetches materiality data for a specific reporting unit
     * @param {Object} payload - Contains reportingUnitId, cycletype, period, reporting_type, and axiosInstance
     * @returns {Object} - The materiality data from the API including GL data map and absolute threshold values
     */
    async fetchMyMateriality({ commit, rootState }, { reportingUnitId, cycletype, period, reporting_type, client, axiosInstance }) {
      commit('SET_MY_MATERIALITY_LOADING', true);
      try {
        const url = varianceAnalysisConstants.API_ENDPOINTS.GET_MY_MATERIALITY + reportingUnitId;
        const params = { cycletype, period, reporting_type };
        if (client) {
          params.cid = client;
        }
        const response = await storeGet(axiosInstance, rootState, url, params);

        console.log('[reportingUnit Store]: fetchMyMateriality', { params });

        if (response?.data?.status !== varianceAnalysisConstants.STATUS.SUCCESS) {
          throw new Error(response?.data?.message || varianceAnalysisConstants.MESSAGES.FAILED_TO_FETCH_MATERIALITY);
        }

        const responseData = response.data.data || [];
        const materialityData = {};
        
        // Build GL data map
        for (const item of responseData) {
          item._id = item.gl_code;
          materialityData[item._id] = item;
        }

        commit('SET_MY_MATERIALITY_DATA', materialityData);

        // Store absolute threshold values
        const absoluteThresholdValues = response.data.absolute_threshold_values || {};
        commit('SET_MY_MATERIALITY_ABSOLUTE_THRESHOLD_VALUES', absoluteThresholdValues);

        // Extract RU/GL/SL level materiality data from API response
        let ruLevelMateriality = {};
        let glLevelMateriality = {};
        let slLevelMateriality = {};
        
        if (responseData.length > 0) {
          ruLevelMateriality = responseData[0]?.ruLevelMateriality || {};
          glLevelMateriality = responseData[0]?.glLevelMateriality || {};
          slLevelMateriality = responseData[0]?.slLevelMateriality || {};
        }

        // Commit materiality level data to store
        commit('SET_VIEW_RU_LEVEL_MATERIALITY_DATA', ruLevelMateriality);
        commit('SET_VIEW_GL_LEVEL_MATERIALITY_DATA', glLevelMateriality);
        commit('SET_VIEW_SL_LEVEL_MATERIALITY_DATA', slLevelMateriality);

        // Return all metadata needed by the component
        return {
          data: responseData,
          glDataMap: materialityData,
          absoluteThresholdValues,
          ruLevelMateriality,
          glLevelMateriality,
          slLevelMateriality,
          hasData: responseData.length > 0
        };
      } catch (error) {
        console.error('[reportingUnit] fetchMyMateriality error:', error);
        commit('sbError', error.message || varianceAnalysisConstants.MESSAGES.FAILED_TO_FETCH_MATERIALITY, { root: true });
        throw error;
      } finally {
        commit('SET_MY_MATERIALITY_LOADING', false);
      }
    },

    /**
     * Fetch full list of reporting units for dropdowns/validation
     * @param {Object} payload - Contains axiosInstance
     */
    async fetchFullReportingUnitList({ commit, rootState }, { axiosInstance }) {      
      try {
        const response = await storeGet(
          axiosInstance, rootState,
          varianceAnalysisConstants.API_ENDPOINTS.GET_ALL_REPORTING_UNITS
        );
        
        if (response?.data?.status === varianceAnalysisConstants.STATUS.SUCCESS) {
          return response.data.data || [];
        }
        throw new Error(response?.data?.message || 'Failed to fetch reporting units');
      } catch (error) {
        console.error('[StoreAction]: fetchFullReportingUnitList error', error);
        commit('sbError', error.message || 'Failed to fetch reporting units', { root: true });
        throw error;
      }
    },

    /**
     * Upload bulk workflow file
     */
    async uploadBulkFileWorkflow({ commit }, { payload, axiosInstance }) {
      try {
        const response = await axiosInstance.post(
          varianceAnalysisConstants.API_ENDPOINTS.ADD_BULK_WORKFLOW,
          { workflowDetails: payload }
        );
        
        if (response.data.status === varianceAnalysisConstants.STATUS.SUCCESS) {
          commit('sbSuccess', "Bulk Enable/Disable Workflow added", { root: true });
          return true;
        }
        throw new Error(response.data.message || "Error adding Bulk Enable/Disable Workflow");
      } catch (error) {
        console.error('[StoreAction]: uploadBulkFileWorkflow error', error);
        commit('sbError', error.message || "Error adding Bulk Enable/Disable Workflow", { root: true });
        throw error;
      }
    },

    /**
     * Save materiality threshold updates (RU, GL, or SL level)
     * @param {Object} payload - Contains data array with materiality update information
     * @param {string} payload.type - Type of update: 'materialityrulevel', 'materialitygllevel', or 'materialitysllevel'
     * @param {Object} axiosInstance - Axios instance for API calls
     */
    async saveMaterialityThreshold({ commit }, { payload, axiosInstance }) {
      try {
        const response = await axiosInstance.post(
          '/v2/varianceanalysis/materiality/saveupdateforapproval',
          { data: [payload] }
        );

        if (response?.data?.status === varianceAnalysisConstants.STATUS.SUCCESS) {
          // Determine success message based on type
          let successMessage = 'Materiality request raised successfully';
          if (payload.type === 'materialityrulevel' || payload.type === 'bulkmaterialityrulevel') {
            successMessage = payload.multiplematerialities
              ? 'Multiple materiality request raised successfully'
              : 'RU level materiality request raised successfully';
          } else if (payload.type === 'materialitygllevel' || payload.type === 'bulkmaterialitygllevel') {
            successMessage = 'GL level materiality request raised successfully';
          } else if (payload.type === 'materialitysllevel' || payload.type === 'bulkmaterialitysllevel') {
            successMessage = 'Drill-down level materiality request raised successfully';
          }

          commit('sbSuccess', successMessage, { root: true });
          return response.data;
        } else {
          throw new Error(response?.data?.message || 'Failed to update materiality threshold');
        }
      } catch (error) {
        console.error('[reportingUnit] saveMaterialityThreshold error:', error);
        const errorMessage = error.response?.data?.message || error.message || 'Failed to update materiality threshold';
        commit('sbError', errorMessage, { root: true });
        throw error;
      }
    },
  },

  getters: {
    getReportingUnitSettings(state) {
      return state?.reportingunitWorkflowSettings || null;
    },
    getRUFilters(state) {
      return state.ruFilters;
    },
    getMyReportingUnits(state) {
      return state.myReportingUnits;
    },
    getMyReportingUnitsMeta(state) {
      return state.myReportingUnitsMeta;
    },
    getMaterialityData(state) {
      return state.materialityData;
    },
    getRUDetail(state) {
      return state.ruDetail;
    },
    getEditingMaterialityPayload(state) {
      return state.editingMaterialityPayload;
    },
    getMaterialityGLList(state) {
      return state.materialityGLList;
    },
    getMaterialityFormattedGLData(state) {
      return state.materialityFormattedGLData;
    },
    getMaterialityAddeditGLData(state) {
      return state.materialityAddeditGLData;
    },
    getMaterialityFormattedRUData(state) {
      return state.materialityFormattedRUData;
    },
    getMaterialityFormattedRUErrorData(state) {
      return state.materialityFormattedRUErrorData;
    },
    getViewRULevelMaterialityData(state) {
      return state.viewRULevelMaterialityData;
    },
    getViewGLLevelMaterialityData(state) {
      return state.viewGLLevelMaterialityData;
    },
    getViewSLLevelMaterialityData(state) {
      return state.viewSLLevelMaterialityData;
    },
    // Threshold page getters
    getAllReportingUnits(state) {
      return state.allReportingUnits;
    },
    getAllReportingUnitsMeta(state) {
      return state.allReportingUnitsMeta;
    },
    getGLList(state) {
      return state.glList;
    },
    getGLCodeNameMap(state) {
      return state.glCodeNameMap;
    },
    getGLReportingTypeMap(state) {
      return state.glReportingTypeMap;
    },
    getGLCodeIdMap(state) {
      return state.glCodeIdMap;
    },
    getAllGLs(state) {
      return state.allGLs;
    },
    getPendingApprovalRequests(state) {
      return state.pendingApprovalRequests;
    },
    getMyMaterialityData(state) {
      return state.myMaterialityData;
    },
    getMyMaterialityAbsoluteThresholdValues(state) {
      return state.myMaterialityAbsoluteThresholdValues;
    },
    getMyMaterialityLoading(state) {
      return state.myMaterialityLoading;
    },
    getRUInsights(state) {
      return state.ruInsights;
    },
    getRUInsightsLoading(state) {
      return state.ruInsightsLoading;
    }
  },
};